package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.AccountantExport
import com.evoapps.evoshift.domain.model.AccountantExportFormat
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.reports.ExportForAccountantUseCase
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.print_not_allowed
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class AccountantExportState(
    val format: AccountantExportFormat = AccountantExportFormat.TIME_RECORD_PDF,
    /** The accountant's address as typed; blank: download only. */
    val emailTo: String = "",
    val working: Boolean = false,
    /** The file is ready (and e-mailed when [AccountantExport.emailedTo] is set): the link is offered. */
    val ready: AccountantExport? = null,
    val error: UiText? = null
) {
    val canSend: Boolean get() = emailTo.isNotBlank() && !working
}

/**
 * "For the accountant" on the Labour cost screen (SH-117): the month's working time as the record,
 * the attendance list or an import file, built by the server and downloaded through a 24-hour link
 * (opened on the person's tap) or e-mailed to the accountant. Owners and financial managers only
 * (Gate.PAYROLL); the server checks the same.
 */
class AccountantExportViewModel(
    private val rosterId: String,
    private val exportForAccountant: ExportForAccountantUseCase,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val observePlanActive: ObservePlanActiveUseCase? = null
) : ViewModel() {
    private val _state = MutableStateFlow(AccountantExportState())
    val state: StateFlow<AccountantExportState> = _state.asStateFlow()

    fun choose(format: AccountantExportFormat) = _state.update { if (it.working) it else it.copy(format = format, ready = null, error = null) }

    fun updateEmail(value: String) = _state.update { it.copy(emailTo = value, error = null) }

    fun download() = run(emailTo = null)

    fun send() {
        if (_state.value.emailTo.isBlank()) return
        run(emailTo = _state.value.emailTo)
    }

    /** A fresh dialog keeps the format and address typed before, not the last result. */
    fun reset() {
        if (!_state.value.working) _state.update { it.copy(ready = null, error = null) }
    }

    private fun run(emailTo: String?) {
        if (_state.value.working) return
        val format = _state.value.format
        _state.update { it.copy(working = true, ready = null, error = null) }
        viewModelScope.launch {
            val workplaceId = RosterIds.workplaceIdOf(rosterId)
            val allowed = capabilitiesAt(getCurrentUser(), observePlanActive, workplaceId).first().shows(Gate.PAYROLL)
            if (!allowed) {
                _state.update { it.copy(working = false, error = UiText.Resource(Res.string.print_not_allowed)) }
                return@launch
            }
            exportForAccountant(rosterId, format, emailTo).fold(
                onSuccess = { export -> _state.update { it.copy(working = false, ready = export) } },
                onFailure = { e -> _state.update { it.copy(working = false, error = e.toUiText()) } }
            )
        }
    }
}
